import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createApiClient } from './api';

export const TOKEN_KEY = 'operations_token';
export const AUTH_CACHE_KEY = 'operations_auth_user_v1';
const CACHE_MAX_AGE = 24 * 60 * 60 * 1_000;
const VALID_ROLES = new Set(['staff', 'manager', 'operations', 'director']);

const AuthContext = createContext(null);

function tokenFingerprint(token) {
  let hash = 2166136261;
  for (let index = 0; index < String(token || '').length; index += 1) {
    hash ^= token.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return token ? `${token.length}:${hash >>> 0}` : '';
}

export function normalizeUser(user = {}) {
  const venues = user.venues || user.assigned_venues || [];
  return {
    ...user,
    id: user.id || user._id || user.email,
    full_name: user.full_name || user.name || user.email || 'User',
    role: String(user.role || '').toLowerCase(),
    venues: Array.isArray(venues) ? venues : [],
    must_change_password: false,
  };
}

function validCachedUser(user) {
  return Boolean(
    user
    && typeof user.email === 'string'
    && VALID_ROLES.has(user.role)
    && Array.isArray(user.venues)
    && typeof user.must_change_password === 'boolean',
  );
}

export function readCachedUser(token, now = Date.now(), storage = localStorage) {
  if (!token) return null;
  try {
    const cached = JSON.parse(storage.getItem(AUTH_CACHE_KEY));
    if (
      cached?.tokenFingerprint !== tokenFingerprint(token)
      || !Number.isFinite(cached?.cachedAt)
      || now < cached.cachedAt
      || now - cached.cachedAt > CACHE_MAX_AGE
      || !validCachedUser(cached.user)
    ) return null;
    return normalizeUser(cached.user);
  } catch {
    return null;
  }
}

export function writeCachedUser(token, user, now = Date.now(), storage = localStorage) {
  const normalized = normalizeUser(user);
  if (!token || !validCachedUser(normalized)) return;
  storage.setItem(AUTH_CACHE_KEY, JSON.stringify({
    tokenFingerprint: tokenFingerprint(token),
    cachedAt: now,
    user: normalized,
  }));
}

export function clearStoredAuth(storage = localStorage) {
  storage.removeItem(TOKEN_KEY);
  storage.removeItem(AUTH_CACHE_KEY);
}

/**
 * Validate and persist a complete login-shaped session before React applies it. If either
 * storage write fails, best-effort rollback keeps the previous token/cache pair together.
 */
export function persistSession(response, now = Date.now(), storage = localStorage) {
  const nextToken = response?.access_token || response?.token;
  if (!nextToken) throw new Error('The session response did not include an access token.');
  if (!response?.user) throw new Error('The session response did not include a user.');

  const nextUser = normalizeUser(response.user);
  if (!validCachedUser(nextUser)) {
    throw new Error('The session response included an invalid user.');
  }

  const previousToken = storage.getItem(TOKEN_KEY);
  const previousCache = storage.getItem(AUTH_CACHE_KEY);
  try {
    storage.setItem(TOKEN_KEY, nextToken);
    writeCachedUser(nextToken, nextUser, now, storage);
  } catch (error) {
    try {
      if (previousToken === null) storage.removeItem(TOKEN_KEY);
      else storage.setItem(TOKEN_KEY, previousToken);
      if (previousCache === null) storage.removeItem(AUTH_CACHE_KEY);
      else storage.setItem(AUTH_CACHE_KEY, previousCache);
    } catch {
      // Preserve the original storage failure; rollback is best effort only.
    }
    throw error;
  }

  return { token: nextToken, user: nextUser };
}

export function AuthProvider({ children }) {
  const initialRef = useRef(null);
  if (!initialRef.current) {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    initialRef.current = {
      token: storedToken,
      user: readCachedUser(storedToken),
    };
  }

  const [token, setToken] = useState(initialRef.current.token);
  const [user, setUser] = useState(initialRef.current.user);
  const [loading, setLoading] = useState(Boolean(token && !initialRef.current.user));
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const logout = useCallback(() => {
    clearStoredAuth();
    tokenRef.current = null;
    setToken(null);
    setUser(null);
    setLoading(false);
  }, []);

  const api = useMemo(() => createApiClient({
    getToken: () => tokenRef.current || localStorage.getItem(TOKEN_KEY),
    onUnauthorized: logout,
  }), [logout]);

  const applyUser = useCallback((nextUser, nextToken = tokenRef.current) => {
    const normalized = normalizeUser(nextUser);
    writeCachedUser(nextToken, normalized);
    setUser(normalized);
    return normalized;
  }, []);

  const replaceSession = useCallback((response) => {
    const nextSession = persistSession(response);
    tokenRef.current = nextSession.token;
    setToken(nextSession.token);
    setUser(nextSession.user);
    setLoading(false);
    return nextSession.user;
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    api.get('/auth/me', { signal: controller.signal })
      .then((nextUser) => applyUser(nextUser, token))
      .catch((error) => {
        if (error?.name !== 'AbortError' && error?.status !== 401) setLoading(false);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [api, applyUser, token]);

  const login = useCallback(async (email, password) => {
    const result = await api.post('/auth/login', { email, password });
    if (result?.user) return replaceSession(result);

    // Retain compatibility with older deployments that returned only a token.
    const nextToken = result?.access_token || result?.token;
    if (!nextToken) throw new Error('The sign-in response did not include an access token.');
    localStorage.setItem(TOKEN_KEY, nextToken);
    tokenRef.current = nextToken;
    setToken(nextToken);
    const nextUser = result.user || await api.get('/auth/me');
    return applyUser(nextUser, nextToken);
  }, [api, applyUser, replaceSession]);

  const refreshUser = useCallback(async () => {
    const nextUser = await api.get('/auth/me');
    return applyUser(nextUser);
  }, [api, applyUser]);

  const value = useMemo(() => ({
    api,
    loading,
    login,
    logout,
    refreshUser,
    replaceSession,
    token,
    user,
  }), [api, loading, login, logout, refreshUser, replaceSession, token, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
